import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight, Loader2, Plug, CheckCircle2, XCircle } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import {
  getSapCustomers, createSapCustomer,
  getSapSystems, createSapSystem,
  getSapConnections, createSapConnection,
  testSapConnection,
} from '../../core/api/apiService';

const ENVIRONMENTS = ['dev', 'test', 'sandbox', 'prod'];
const CONNECTION_TYPES = ['zrest', 'soap', 'rfc', 'odata'];

/**
 * SAP-Konnektivitaet: Kunde -> System (Umgebung) -> Verbindung (Transportart), DB-gestuetzt
 * (sap_customers/sap_systems/sap_system_connections). Ersetzt schrittweise die verstreuten
 * ADAPTER_SAP_*-ENV-Variablen (Fallback: ADAPTER_SBX_*) als Konfigurationsquelle (s. sapConnectionResolver.js). Bewusst ein
 * einfaches Anlegen-Formular je Ebene statt voller Inline-Edit-UI — POC-Stand, s. PR-Beschreibung.
 */
const SapConnectivityPanel = ({ user, showSuccess, showError }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [newCustomer, setNewCustomer] = useState({ customerCode: '', customerName: '', contactName: '', contactEmail: '', contactPhone: '' });

  const [expandedCustomerId, setExpandedCustomerId] = useState(null);
  const [systemsByCustomer, setSystemsByCustomer] = useState({});
  const [newSystemByCustomer, setNewSystemByCustomer] = useState({});

  const [expandedSystemId, setExpandedSystemId] = useState(null);
  const [connectionsBySystem, setConnectionsBySystem] = useState({});
  const [newConnectionBySystem, setNewConnectionBySystem] = useState({});

  const [testingConnectionId, setTestingConnectionId] = useState(null);

  const cardClass = `p-6 rounded-2xl border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`;
  const inputClass = `w-full px-3 py-2 rounded-lg border text-sm ${
    isDarkMode ? 'bg-white/5 border-white/20 text-white placeholder-white/40' : 'bg-white border-gray-300 text-gray-900'
  }`;
  const buttonClass = (busy) => `inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
    busy ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-brand text-white hover:opacity-90'
  }`;
  const subCardClass = `p-4 rounded-xl border ${isDarkMode ? 'bg-white/[0.03] border-white/10' : 'bg-gray-50 border-gray-200'}`;
  // Native <select> dropdown popups don't reliably inherit the parent's text color in every
  // browser — Chromium DOES propagate it but keeps the popup background browser-default white,
  // which silently makes light "text-white" options invisible (white-on-white). Explicit
  // bg+text per <option>, matching the pattern already used in UserForm.js.
  const optionClass = isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800';

  const loadCustomers = useCallback(async () => {
    setLoadingCustomers(true);
    try {
      setCustomers(await getSapCustomers(user));
    } catch (error) {
      showError?.(error.message || t('admin.sapConnectivity.error'));
    } finally {
      setLoadingCustomers(false);
    }
  }, [user, showError, t]);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  const handleCreateCustomer = async () => {
    if (!newCustomer.customerCode) return;
    try {
      await createSapCustomer(user, newCustomer);
      setNewCustomer({ customerCode: '', customerName: '', contactName: '', contactEmail: '', contactPhone: '' });
      showSuccess?.(t('admin.sapConnectivity.success'));
      loadCustomers();
    } catch (error) {
      showError?.(error.message || t('admin.sapConnectivity.error'));
    }
  };

  const toggleCustomer = async (customerId) => {
    if (expandedCustomerId === customerId) {
      setExpandedCustomerId(null);
      return;
    }
    setExpandedCustomerId(customerId);
    setExpandedSystemId(null);
    if (!systemsByCustomer[customerId]) {
      try {
        const systems = await getSapSystems(user, customerId);
        setSystemsByCustomer((prev) => ({ ...prev, [customerId]: systems }));
      } catch (error) {
        showError?.(error.message || t('admin.sapConnectivity.error'));
      }
    }
  };

  const handleCreateSystem = async (customerId) => {
    const fields = newSystemByCustomer[customerId];
    if (!fields || !fields.environment || !fields.sid || !fields.sapMandant || !fields.host) return;
    try {
      await createSapSystem(user, customerId, fields);
      setNewSystemByCustomer((prev) => ({ ...prev, [customerId]: {} }));
      showSuccess?.(t('admin.sapConnectivity.success'));
      const systems = await getSapSystems(user, customerId);
      setSystemsByCustomer((prev) => ({ ...prev, [customerId]: systems }));
    } catch (error) {
      showError?.(error.message || t('admin.sapConnectivity.error'));
    }
  };

  const toggleSystem = async (systemId) => {
    if (expandedSystemId === systemId) {
      setExpandedSystemId(null);
      return;
    }
    setExpandedSystemId(systemId);
    if (!connectionsBySystem[systemId]) {
      try {
        const connections = await getSapConnections(user, systemId);
        setConnectionsBySystem((prev) => ({ ...prev, [systemId]: connections }));
      } catch (error) {
        showError?.(error.message || t('admin.sapConnectivity.error'));
      }
    }
  };

  const handleCreateConnection = async (systemId) => {
    const fields = newConnectionBySystem[systemId];
    if (!fields || !fields.connectionType || !fields.baseUrl) return;
    try {
      await createSapConnection(user, systemId, fields);
      setNewConnectionBySystem((prev) => ({ ...prev, [systemId]: {} }));
      showSuccess?.(t('admin.sapConnectivity.success'));
      const connections = await getSapConnections(user, systemId);
      setConnectionsBySystem((prev) => ({ ...prev, [systemId]: connections }));
    } catch (error) {
      showError?.(error.message || t('admin.sapConnectivity.error'));
    }
  };

  const handleTestConnection = async (systemId, connectionId) => {
    setTestingConnectionId(connectionId);
    try {
      await testSapConnection(user, connectionId);
      const connections = await getSapConnections(user, systemId);
      setConnectionsBySystem((prev) => ({ ...prev, [systemId]: connections }));
    } catch (error) {
      showError?.(error.message || t('admin.sapConnectivity.error'));
    } finally {
      setTestingConnectionId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-500'}`}>
        <Plug className="w-4 h-4" />
        {t('admin.sapConnectivity.intro')}
      </div>

      {/* Neuen Kunden anlegen */}
      <div className={cardClass}>
        <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('admin.sapConnectivity.newCustomerTitle')}</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
          <input className={inputClass} placeholder={t('admin.sapConnectivity.customerCode')} value={newCustomer.customerCode}
            onChange={(e) => setNewCustomer({ ...newCustomer, customerCode: e.target.value })} />
          <input className={inputClass} placeholder={t('admin.sapConnectivity.customerName')} value={newCustomer.customerName}
            onChange={(e) => setNewCustomer({ ...newCustomer, customerName: e.target.value })} />
          <input className={inputClass} placeholder={t('admin.sapConnectivity.contactName')} value={newCustomer.contactName}
            onChange={(e) => setNewCustomer({ ...newCustomer, contactName: e.target.value })} />
          <input className={inputClass} placeholder={t('admin.sapConnectivity.contactEmail')} value={newCustomer.contactEmail}
            onChange={(e) => setNewCustomer({ ...newCustomer, contactEmail: e.target.value })} />
          <input className={inputClass} placeholder={t('admin.sapConnectivity.contactPhone')} value={newCustomer.contactPhone}
            onChange={(e) => setNewCustomer({ ...newCustomer, contactPhone: e.target.value })} />
        </div>
        <button className={buttonClass(false)} disabled={!newCustomer.customerCode} onClick={handleCreateCustomer}>
          {t('admin.sapConnectivity.addCustomerButton')}
        </button>
      </div>

      {/* Kundenliste */}
      <div className={cardClass}>
        <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('admin.sapConnectivity.customersTitle')}</h3>
        {loadingCustomers ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : customers.length === 0 ? (
          <p className={`text-sm ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>{t('admin.sapConnectivity.noCustomers')}</p>
        ) : (
          <div className="space-y-3">
            {customers.map((c) => (
              <div key={c.id} className={subCardClass}>
                <button className="w-full flex items-center justify-between text-left" onClick={() => toggleCustomer(c.id)}>
                  <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {c.customer_code} {c.customer_name ? `— ${c.customer_name}` : ''}
                  </span>
                  {expandedCustomerId === c.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>

                {expandedCustomerId === c.id && (
                  <div className="mt-4 space-y-3 pl-2 border-l-2 border-brand/30">
                    {(systemsByCustomer[c.id] || []).map((s) => (
                      <div key={s.id} className={subCardClass}>
                        <button className="w-full flex items-center justify-between text-left" onClick={() => toggleSystem(s.id)}>
                          <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {s.environment} — {s.sid} (mandant {s.sap_mandant}) — {s.host}:{s.port}
                          </span>
                          {expandedSystemId === s.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>

                        {expandedSystemId === s.id && (
                          <div className="mt-3 space-y-2 pl-2 border-l-2 border-brand/20">
                            {(connectionsBySystem[s.id] || []).map((conn) => (
                              <div key={conn.id} className={`flex items-center justify-between p-3 rounded-lg text-sm ${isDarkMode ? 'bg-black/20' : 'bg-white border border-gray-200'}`}>
                                <div>
                                  <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                    {conn.connection_type} {conn.is_default ? `(${t('admin.sapConnectivity.default')})` : ''}
                                    {conn.has_stored_password && (
                                      <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-white/10 text-white/60' : 'bg-gray-100 text-gray-500'}`}>
                                        {t('admin.sapConnectivity.passwordStored')}
                                      </span>
                                    )}
                                  </div>
                                  <div className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>
                                    {conn.public_url || conn.base_url}
                                  </div>
                                  {conn.last_tested_at && (
                                    <div className={`text-xs flex items-center gap-1 mt-1 ${conn.last_test_result === 'ok' ? 'text-green-500' : 'text-red-500'}`}>
                                      {conn.last_test_result === 'ok' ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                                      {conn.last_test_message}
                                    </div>
                                  )}
                                </div>
                                <button
                                  className={buttonClass(testingConnectionId === conn.id)}
                                  disabled={testingConnectionId === conn.id}
                                  onClick={() => handleTestConnection(s.id, conn.id)}
                                >
                                  {testingConnectionId === conn.id && <Loader2 className="w-4 h-4 animate-spin" />}
                                  {t('admin.sapConnectivity.testButton')}
                                </button>
                              </div>
                            ))}

                            {/* Neue Verbindung anlegen */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
                              <select className={inputClass}
                                value={(newConnectionBySystem[s.id] || {}).connectionType || ''}
                                onChange={(e) => setNewConnectionBySystem((prev) => ({ ...prev, [s.id]: { ...prev[s.id], connectionType: e.target.value } }))}>
                                <option value="" className={optionClass}>{t('admin.sapConnectivity.connectionType')}</option>
                                {CONNECTION_TYPES.map((ct) => <option key={ct} value={ct} className={optionClass}>{ct}</option>)}
                              </select>
                              <input className={inputClass} placeholder={t('admin.sapConnectivity.baseUrl')}
                                value={(newConnectionBySystem[s.id] || {}).baseUrl || ''}
                                onChange={(e) => setNewConnectionBySystem((prev) => ({ ...prev, [s.id]: { ...prev[s.id], baseUrl: e.target.value } }))} />
                              <input className={inputClass} placeholder={t('admin.sapConnectivity.publicUrl')}
                                value={(newConnectionBySystem[s.id] || {}).publicUrl || ''}
                                onChange={(e) => setNewConnectionBySystem((prev) => ({ ...prev, [s.id]: { ...prev[s.id], publicUrl: e.target.value } }))} />
                              <input className={inputClass} placeholder={t('admin.sapConnectivity.technicalUser')}
                                value={(newConnectionBySystem[s.id] || {}).technicalUser || ''}
                                onChange={(e) => setNewConnectionBySystem((prev) => ({ ...prev, [s.id]: { ...prev[s.id], technicalUser: e.target.value } }))} />
                              <input className={inputClass} placeholder={t('admin.sapConnectivity.credentialRef')}
                                value={(newConnectionBySystem[s.id] || {}).credentialRef || ''}
                                onChange={(e) => setNewConnectionBySystem((prev) => ({ ...prev, [s.id]: { ...prev[s.id], credentialRef: e.target.value } }))} />
                              <input className={inputClass} type="password" autoComplete="new-password" placeholder={t('admin.sapConnectivity.password')}
                                value={(newConnectionBySystem[s.id] || {}).password || ''}
                                onChange={(e) => setNewConnectionBySystem((prev) => ({ ...prev, [s.id]: { ...prev[s.id], password: e.target.value } }))} />
                            </div>
                            <p className={`text-xs mt-1 mb-2 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>
                              {t('admin.sapConnectivity.passwordHint')}
                            </p>
                            <button className={buttonClass(false)} onClick={() => handleCreateConnection(s.id)}>
                              {t('admin.sapConnectivity.addConnectionButton')}
                            </button>
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Neues System anlegen */}
                    <div className={subCardClass}>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-2">
                        <select className={inputClass}
                          value={(newSystemByCustomer[c.id] || {}).environment || ''}
                          onChange={(e) => setNewSystemByCustomer((prev) => ({ ...prev, [c.id]: { ...prev[c.id], environment: e.target.value } }))}>
                          <option value="" className={optionClass}>{t('admin.sapConnectivity.environment')}</option>
                          {ENVIRONMENTS.map((env) => <option key={env} value={env} className={optionClass}>{env}</option>)}
                        </select>
                        <input className={inputClass} placeholder="SID" value={(newSystemByCustomer[c.id] || {}).sid || ''}
                          onChange={(e) => setNewSystemByCustomer((prev) => ({ ...prev, [c.id]: { ...prev[c.id], sid: e.target.value } }))} />
                        <input className={inputClass} placeholder={t('admin.sapConnectivity.mandant')} value={(newSystemByCustomer[c.id] || {}).sapMandant || ''}
                          onChange={(e) => setNewSystemByCustomer((prev) => ({ ...prev, [c.id]: { ...prev[c.id], sapMandant: e.target.value } }))} />
                        <input className={inputClass} placeholder="Host" value={(newSystemByCustomer[c.id] || {}).host || ''}
                          onChange={(e) => setNewSystemByCustomer((prev) => ({ ...prev, [c.id]: { ...prev[c.id], host: e.target.value } }))} />
                        <input className={inputClass} placeholder="Port" value={(newSystemByCustomer[c.id] || {}).port || ''}
                          onChange={(e) => setNewSystemByCustomer((prev) => ({ ...prev, [c.id]: { ...prev[c.id], port: e.target.value } }))} />
                      </div>
                      <button className={buttonClass(false)} onClick={() => handleCreateSystem(c.id)}>
                        {t('admin.sapConnectivity.addSystemButton')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SapConnectivityPanel;
